import { Language } from '../types/auth.types';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { app } from '../config/firebase.config';

// Log sécurisé pour confirmer l'initialisation
console.log('📧 Email Service initialized');

// Initialiser les fonctions Firebase
const functions = getFunctions(app);

// Connecter à l'émulateur en mode développement
if (process.env.NODE_ENV === 'development') {
  console.log('📧 Connexion à l\'\u00e9mulateur Firebase Functions');
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

interface EmailTemplate {
  subject: string;
  content: (...args: any[]) => string;
}

const welcomeTemplates: Record<Language, EmailTemplate> = {
  fr: {
    subject: '⚽ Bienvenue sur TacticaFoot !',
    content: (name: string, baseUrl: string, logoUrl: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <img src="${logoUrl}" alt="TacticaFoot Logo" style="max-width: 180px; height: auto;" />
        </div>
        <h1 style="color: #1976d2; text-align: center;">⚽ Bienvenue sur TacticaFoot, ${name} !</h1>
        <p>Bonjour ${name},</p>
        <p>Nous sommes ravis de vous accueillir sur TacticaFoot, votre plateforme dédiée à la création et gestion de vos séquences, compositions et exercices de football de manière intuitive et efficace.</p>
        <ul style="list-style-type: none; padding-left: 0; margin: 20px 0;">
          <li style="margin: 10px 0;">✅ Créez et ajustez vos stratégies en quelques clics</li>
          <li style="margin: 10px 0;">📊 Analysez et optimisez vos compositions d'équipe</li>
          <li style="margin: 10px 0;">🎥 Exportez et partagez vos animations en toute simplicité</li>
        </ul>
        <p style="margin: 30px 0; text-align: center;">
          <a href="${baseUrl}/app" style="display: inline-block; padding: 12px 24px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">
            🔥 Commencer maintenant
          </a>
        </p>
        <p>Si vous avez la moindre question, notre équipe est là pour vous accompagner.</p>
        <p>Bonne création et bon match ! ⚡</p>
        <p>Sportivement,<br>L'équipe TacticaFoot</p>
      </div>
    `
  },
  en: {
    subject: '⚽ Welcome to TacticaFoot!',
    content: (name: string, baseUrl: string, logoUrl: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <img src="${logoUrl}" alt="TacticaFoot Logo" style="max-width: 180px; height: auto;" />
        </div>
        <h1 style="color: #1976d2; text-align: center;">⚽ Welcome to TacticaFoot, ${name}!</h1>
        <p>Hello ${name},</p>
        <p>We're excited to welcome you to TacticaFoot, your dedicated platform for creating and managing football sequences, compositions, and exercises intuitively and efficiently.</p>
        <ul style="list-style-type: none; padding-left: 0; margin: 20px 0;">
          <li style="margin: 10px 0;">✅ Create and adjust your strategies in just a few clicks</li>
          <li style="margin: 10px 0;">📊 Analyze and optimize your team compositions</li>
          <li style="margin: 10px 0;">🎥 Export and share your animations with ease</li>
        </ul>
        <p style="margin: 30px 0; text-align: center;">
          <a href="${baseUrl}/app" style="display: inline-block; padding: 12px 24px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">
            🔥 Get Started Now
          </a>
        </p>
        <p>If you have any questions, our team is here to help.</p>
        <p>Happy creating and good game! ⚡</p>
        <p>Best regards,<br>The TacticaFoot Team</p>
      </div>
    `
  },
  es: {
    subject: '⚽ ¡Bienvenido a TacticaFoot!',
    content: (name: string, baseUrl: string, logoUrl: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <img src="${logoUrl}" alt="TacticaFoot Logo" style="max-width: 180px; height: auto;" />
        </div>
        <h1 style="color: #1976d2; text-align: center;">⚽ ¡Bienvenido a TacticaFoot, ${name}!</h1>
        <p>Hola ${name},</p>
        <p>Nos complace darte la bienvenida a TacticaFoot, tu plataforma dedicada a crear y gestionar secuencias, composiciones y ejercicios de fútbol de manera intuitiva y eficiente.</p>
        <ul style="list-style-type: none; padding-left: 0; margin: 20px 0;">
          <li style="margin: 10px 0;">✅ Crea y ajusta tus estrategias con pocos clics</li>
          <li style="margin: 10px 0;">📊 Analiza y optimiza tus composiciones de equipo</li>
          <li style="margin: 10px 0;">🎥 Exporta y comparte tus animaciones fácilmente</li>
        </ul>
        <p style="margin: 30px 0; text-align: center;">
          <a href="${baseUrl}/app" style="display: inline-block; padding: 12px 24px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">
            🔥 Empezar ahora
          </a>
        </p>
        <p>Si tienes alguna pregunta, nuestro equipo está aquí para ayudarte.</p>
        <p>¡Feliz creación y buen partido! ⚡</p>
        <p>Saludos deportivos,<br>El Equipo TacticaFoot</p>
      </div>
    `
  },
  ru: {
    subject: '⚽ Добро пожаловать в TacticaFoot!',
    content: (name: string, baseUrl: string, logoUrl: string) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <img src="${logoUrl}" alt="TacticaFoot Logo" style="max-width: 180px; height: auto;" />
        </div>
        <h1 style="color: #1976d2; text-align: center;">⚽ Добро пожаловать в TacticaFoot, ${name}!</h1>
        <p>Здравствуйте, ${name},</p>
        <p>Мы рады приветствовать вас в TacticaFoot, вашей платформе для создания и управления футбольными последовательностями, составами и упражнениями интуитивно и эффективно.</p>
        <ul style="list-style-type: none; padding-left: 0; margin: 20px 0;">
          <li style="margin: 10px 0;">✅ Создавайте и корректируйте стратегии в несколько кликов</li>
          <li style="margin: 10px 0;">📊 Анализируйте и оптимизируйте составы команды</li>
          <li style="margin: 10px 0;">🎥 Экспортируйте и делитесь анимациями с легкостью</li>
        </ul>
        <p style="margin: 30px 0; text-align: center;">
          <a href="${baseUrl}/app" style="display: inline-block; padding: 12px 24px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">
            🔥 Начать сейчас
          </a>
        </p>
        <p>Если у вас есть вопросы, наша команда готова помочь.</p>
        <p>Удачного создания и хорошей игры! ⚡</p>
        <p>С уважением,<br>Команда TacticaFoot</p>
      </div>
    `
  }
};

const subscriptionTemplates: Record<Language, EmailTemplate> = {
  fr: {
    subject: 'Confirmation de votre abonnement',
    content: (details: any) => `
      <h1>Confirmation d'abonnement</h1>
      <p>Votre abonnement a été activé avec succès.</p>
      <p>Détails de l'abonnement :</p>
      <ul>
        <li>Type : ${details.type}</li>
        <li>Durée : ${details.duration}</li>
      </ul>
      <p>Merci de votre confiance !</p>
    `
  },
  en: {
    subject: 'Subscription Confirmation',
    content: (details: any) => `
      <h1>Subscription Confirmation</h1>
      <p>Your subscription has been successfully activated.</p>
      <p>Subscription details:</p>
      <ul>
        <li>Type: ${details.type}</li>
        <li>Duration: ${details.duration}</li>
      </ul>
      <p>Thank you for your trust!</p>
    `
  },
  es: {
    subject: 'Confirmación de suscripción',
    content: (details: any) => `
      <h1>Confirmación de suscripción</h1>
      <p>Su suscripción se ha activado con éxito.</p>
      <p>Detalles de la suscripción:</p>
      <ul>
        <li>Tipo: ${details.type}</li>
        <li>Duración: ${details.duration}</li>
      </ul>
      <p>¡Gracias por su confianza!</p>
    `
  },
  ru: {
    subject: 'Подтверждение подписки',
    content: (details: any) => `
      <h1>Подтверждение подписки</h1>
      <p>Ваша подписка успешно активирована.</p>
      <p>Детали подписки:</p>
      <ul>
        <li>Тип: ${details.type}</li>
        <li>Продолжительность: ${details.duration}</li>
      </ul>
      <p>Спасибо за доверие!</p>
    `
  }
};

const cancellationTemplates: Record<Language, EmailTemplate> = {
  fr: {
    subject: "Confirmation d'annulation",
    content: () => `
      <h1>Annulation confirmée</h1>
      <p>Votre abonnement a été annulé avec succès.</p>
      <p>Nous espérons vous revoir bientôt !</p>
    `
  },
  en: {
    subject: "Cancellation Confirmation",
    content: () => `
      <h1>Cancellation Confirmed</h1>
      <p>Your subscription has been successfully cancelled.</p>
      <p>We hope to see you again soon!</p>
    `
  },
  es: {
    subject: "Confirmación de cancelación",
    content: () => `
      <h1>Cancelación confirmada</h1>
      <p>Su suscripción ha sido cancelada con éxito.</p>
      <p>¡Esperamos verte pronto!</p>
    `
  },
  ru: {
    subject: "Подтверждение отмены",
    content: () => `
      <h1>Отмена подтверждена</h1>
      <p>Ваша подписка успешно отменена.</p>
      <p>Надеемся увидеть вас снова!</p>
    `
  }
};

const invitationTemplates: Record<Language, EmailTemplate> = {
  fr: {
    subject: '🚀 Vous avez été invité à rejoindre TacticaFoot !',
    content: (inviteUrl: string) => `
      <h1>🚀 Vous avez été invité à rejoindre TacticaFoot !</h1>
      <p>Bonjour,</p>
      <p>Vous avez été invité à rejoindre TacticaFoot, la plateforme dédiée à la création et gestion de vos séquences, compositions et exercices de football.</p>
      <p>⚡ Rejoignez-nous dès maintenant et commencez à optimiser vos stratégies !</p>
      <p><a href="${inviteUrl}" style="display: inline-block; padding: 10px 20px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px;">👉 Accepter l'invitation</a></p>
      <p>📌 Attention : Ce lien est valable 7 jours.</p>
      <p>Si vous avez des questions, notre équipe est là pour vous aider.</p>
    `
  },
  en: {
    subject: '🚀 You have been invited to join TacticaFoot!',
    content: (inviteUrl: string) => `
      <h1>🚀 You have been invited to join TacticaFoot!</h1>
      <p>Hello,</p>
      <p>You have been invited to join TacticaFoot, the platform dedicated to creating and managing your football sequences, compositions, and exercises.</p>
      <p>⚡ Join us now and start optimizing your strategies!</p>
      <p><a href="${inviteUrl}" style="display: inline-block; padding: 10px 20px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px;">👉 Accept invitation</a></p>
      <p>📌 Note: This link is valid for 7 days.</p>
      <p>If you have any questions, our team is here to help.</p>
    `
  },
  es: {
    subject: '🚀 ¡Has sido invitado a unirte a TacticaFoot!',
    content: (inviteUrl: string) => `
      <h1>🚀 ¡Has sido invitado a unirte a TacticaFoot!</h1>
      <p>Hola,</p>
      <p>Has sido invitado a unirte a TacticaFoot, la plataforma dedicada a crear y gestionar tus secuencias, composiciones y ejercicios de fútbol.</p>
      <p>⚡ ¡Únete ahora y comienza a optimizar tus estrategias!</p>
      <p><a href="${inviteUrl}" style="display: inline-block; padding: 10px 20px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px;">👉 Aceptar invitación</a></p>
      <p>📌 Nota: Este enlace es válido por 7 días.</p>
      <p>Si tienes alguna pregunta, nuestro equipo está aquí para ayudarte.</p>
    `
  },
  ru: {
    subject: '🚀 Вас пригласили присоединиться к TacticaFoot!',
    content: (inviteUrl: string) => `
      <h1>🚀 Вас пригласили присоединиться к TacticaFoot!</h1>
      <p>Здравствуйте,</p>
      <p>Вас пригласили присоединиться к TacticaFoot, платформе для создания и управления футбольными последовательностями, составами и упражнениями.</p>
      <p>⚡ Присоединяйтесь сейчас и начните оптимизировать свои стратегии!</p>
      <p><a href="${inviteUrl}" style="display: inline-block; padding: 10px 20px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px;">👉 Принять приглашение</a></p>
      <p>📌 Примечание: Этот ссылка действительна в течение 7 дней.</p>
      <p>Если у вас есть вопросы, наша команда готова помочь.</p>
    `
  }
};

const trialTemplates: Record<Language, EmailTemplate> = {
  fr: {
    subject: 'Votre accès à TacticaFoot est activé !',
    content: (baseUrl: string, logoUrl: string, endDate: string, durationInDays: number) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <img src="${logoUrl}" alt="TacticaFoot Logo" style="max-width: 180px; height: auto;" />
        </div>
        <h1 style="color: #1976d2; text-align: center;">Votre accès à TacticaFoot est activé !</h1>
        <p>Bonjour,</p>
        <p>Nous sommes ravis de vous informer que votre période d'essai de ${durationInDays} jours sur TacticaFoot est maintenant active !</p>
        <p><strong>Détails de votre période d'essai :</strong></p>
        <ul>
          <li>Durée : ${durationInDays} jours</li>
          <li>Date de fin : ${endDate}</li>
        </ul>
        <p>Pendant ces ${durationInDays} jours, vous avez accès à toutes les fonctionnalités premium de TacticaFoot :</p>
        <ul style="list-style-type: none; padding-left: 0;">
          <li>✅ Création illimitée de séquences et compositions</li>
          <li>✅ Exportation en haute qualité</li>
          <li>✅ Accès à toutes les animations et effets</li>
          <li>✅ Support prioritaire</li>
        </ul>
        <p style="margin: 30px 0; text-align: center;">
          <a href="${baseUrl}/app" style="display: inline-block; padding: 12px 24px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">
            Commencer maintenant →
          </a>
        </p>
        <p>Votre accès expirera automatiquement le ${endDate}. Vous recevrez une notification avant la fin de votre période d'essai.</p>
        <p>Notre équipe est là pour vous aider si vous avez des questions !</p>
        <p>Bonne création,<br>L'équipe TacticaFoot</p>
      </div>
    `
  },
  en: {
    subject: 'Your TacticaFoot access is activated!',
    content: (baseUrl: string, logoUrl: string, endDate: string, durationInDays: number) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <img src="${logoUrl}" alt="TacticaFoot Logo" style="max-width: 180px; height: auto;" />
        </div>
        <h1 style="color: #1976d2; text-align: center;">Your TacticaFoot access is activated!</h1>
        <p>Hello,</p>
        <p>We're excited to inform you that your ${durationInDays}-day trial period on TacticaFoot is now active!</p>
        <p><strong>Trial period details:</strong></p>
        <ul>
          <li>Duration: ${durationInDays} days</li>
          <li>End date: ${endDate}</li>
        </ul>
        <p>During these ${durationInDays} days, you have access to all premium features of TacticaFoot:</p>
        <ul style="list-style-type: none; padding-left: 0;">
          <li>✅ Unlimited creation of sequences and compositions</li>
          <li>✅ High-quality export</li>
          <li>✅ Access to all animations and effects</li>
          <li>✅ Priority support</li>
        </ul>
        <p style="margin: 30px 0; text-align: center;">
          <a href="${baseUrl}/app" style="display: inline-block; padding: 12px 24px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">
            Get Started Now →
          </a>
        </p>
        <p>Your access will automatically expire on ${endDate}. You will receive a notification before your trial period ends.</p>
        <p>Our team is here to help if you have any questions!</p>
        <p>Happy creating,<br>The TacticaFoot Team</p>
      </div>
    `
  },
  es: {
    subject: '¡Tu acceso a TacticaFoot está activado!',
    content: (baseUrl: string, logoUrl: string, endDate: string, durationInDays: number) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <img src="${logoUrl}" alt="TacticaFoot Logo" style="max-width: 180px; height: auto;" />
        </div>
        <h1 style="color: #1976d2; text-align: center;">¡Tu acceso a TacticaFoot está activado!</h1>
        <p>Hola,</p>
        <p>¡Nos complace informarte que tu período de prueba de ${durationInDays} días en TacticaFoot ya está activo!</p>
        <p><strong>Detalles del período de prueba:</strong></p>
        <ul>
          <li>Duración: ${durationInDays} días</li>
          <li>Fecha de finalización: ${endDate}</li>
        </ul>
        <p>Durante estos ${durationInDays} días, tienes acceso a todas las funciones premium de TacticaFoot:</p>
        <ul style="list-style-type: none; padding-left: 0;">
          <li>✅ Creación ilimitada de secuencias y composiciones</li>
          <li>✅ Exportación en alta calidad</li>
          <li>✅ Acceso a todas las animaciones y efectos</li>
          <li>✅ Soporte prioritario</li>
        </ul>
        <p style="margin: 30px 0; text-align: center;">
          <a href="${baseUrl}/app" style="display: inline-block; padding: 12px 24px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">
            Empezar ahora →
          </a>
        </p>
        <p>Tu acceso expirará automáticamente el ${endDate}. Recibirás una notificación antes de que finalice tu período de prueba.</p>
        <p>Nuestro equipo está aquí para ayudarte si tienes alguna pregunta!</p>
        <p>¡Feliz creación,<br>El Equipo TacticaFoot</p>
      </div>
    `
  },
  ru: {
    subject: 'Ваш доступ к TacticaFoot активирован!',
    content: (baseUrl: string, logoUrl: string, endDate: string, durationInDays: number) => `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <img src="${logoUrl}" alt="TacticaFoot Logo" style="max-width: 180px; height: auto;" />
        </div>
        <h1 style="color: #1976d2; text-align: center;">Ваш доступ к TacticaFoot активирован!</h1>
        <p>Здравствуйте,</p>
        <p>Мы рады сообщить, что ваш ${durationInDays}-дневный пробный период в TacticaFoot теперь активен!</p>
        <p><strong>Детали пробного периода:</strong></p>
        <ul>
          <li>Продолжительность: ${durationInDays} дней</li>
          <li>Дата окончания: ${endDate}</li>
        </ul>
        <p>В течение этих ${durationInDays} дней у вас есть доступ ко всем премиум-функциям TacticaFoot:</p>
        <ul style="list-style-type: none; padding-left: 0;">
          <li>✅ Неограниченное создание последовательностей и композиций</li>
          <li>✅ Экспорт в высоком качестве</li>
          <li>✅ Доступ ко всем анимациям и эффектам</li>
          <li>✅ Приоритетная поддержка</li>
        </ul>
        <p style="margin: 30px 0; text-align: center;">
          <a href="${baseUrl}/app" style="display: inline-block; padding: 12px 24px; background-color: #1976d2; color: white; text-decoration: none; border-radius: 4px; font-weight: bold;">
            Начать сейчас →
          </a>
        </p>
        <p>Ваш доступ автоматически истечет ${endDate}. Вы получите уведомление перед окончанием пробного периода.</p>
        <p>Наша команда готова помочь, если у вас есть вопросы!</p>
        <p>Удачного творчества,<br>Команда TacticaFoot</p>
      </div>
    `
  }
};

const getBaseUrl = () => {
  const url = window.location.origin;
  return url.includes('localhost') ? 'http://localhost:3000' : url;
};

// Fonction déclarée dans l'objet EmailService

export const EmailService = {
  async sendEmail(to: string, subject: string, content: string) {
    try {
      console.log('📧 Envoi d\'email en cours via Cloud Function:', { to, subject });
      
      // Extraire le nom du destinataire depuis le contenu HTML (approximatif)
      const nameMatch = content.match(/Bienvenue[^,]*,\s*([^!<]+)/i);
      const name = nameMatch ? nameMatch[1].trim() : 'Utilisateur';
      
      // Déterminer la langue à partir du sujet
      let language: Language = 'fr';
      if (subject.includes('Welcome')) language = 'en';
      else if (subject.includes('Bienvenido')) language = 'es';
      else if (subject.includes('Добро')) language = 'ru';
      
      // Appeler la fonction Cloud Firebase
      const sendWelcomeEmailFn = httpsCallable(functions, 'sendWelcomeEmail');
      const result = await sendWelcomeEmailFn({ 
        email: to, 
        name, 
        language,
        customSubject: subject,
        customHtml: content
      });
      
      console.log('✅ Email envoyé avec succès via Cloud Function:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi d\'email via Cloud Function:', error);
      
      // En mode développement, simuler une réponse réussie pour ne pas bloquer
      if (process.env.NODE_ENV === 'development') {
        console.log('📧 Mode de secours activé pour l\'envoi d\'email en développement');
        return {
          messageId: `simulated-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`,
          status: 'success',
          simulated: true
        };
      }
      
      throw error; // Relancer l'erreur pour la gérer dans le composant
    }
  },

  async sendWelcomeEmail(email: string, name: string, language: Language = 'fr') {
    try {
      console.log('📧 Envoi d\'email de bienvenue via Cloud Function:', { email, name, language });
      
      // Appeler la fonction Cloud Firebase
      const sendWelcomeEmailFn = httpsCallable(functions, 'sendWelcomeEmail');
      const result = await sendWelcomeEmailFn({ email, name, language });
      
      console.log('✅ Email de bienvenue envoyé avec succès via Cloud Function:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi d\'email de bienvenue via Cloud Function:', error);
      throw error; // Relancer l'erreur pour la gérer dans le composant
    }
  },

  async sendSubscriptionConfirmation(email: string, subscriptionDetails: any, language: Language = 'fr') {
    try {
      console.log('📧 Envoi d\'email de confirmation d\'abonnement via Cloud Function:', { email, language });
      
      // Préparer le contenu de l'email
      const template = subscriptionTemplates[language];
      const htmlContent = template.content(subscriptionDetails);
      
      // Appeler la fonction Cloud Firebase
      const sendEmailFn = httpsCallable(functions, 'sendWelcomeEmail');
      const result = await sendEmailFn({ 
        email, 
        name: subscriptionDetails.name || 'Utilisateur', 
        language,
        customSubject: template.subject,
        customHtml: htmlContent
      });
      
      console.log('✅ Email de confirmation d\'abonnement envoyé avec succès:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi d\'email de confirmation d\'abonnement:', error);
      throw error;
    }
  },

  async sendSubscriptionCancellation(email: string, language: Language = 'fr') {
    try {
      console.log('📧 Envoi d\'email d\'annulation d\'abonnement via Cloud Function:', { email, language });
      
      // Préparer le contenu de l'email
      const template = cancellationTemplates[language];
      const htmlContent = template.content();
      
      // Appeler la fonction Cloud Firebase
      const sendEmailFn = httpsCallable(functions, 'sendWelcomeEmail');
      const result = await sendEmailFn({ 
        email, 
        name: 'Utilisateur', 
        language,
        customSubject: template.subject,
        customHtml: htmlContent
      });
      
      console.log('✅ Email d\'annulation d\'abonnement envoyé avec succès:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi d\'email d\'annulation d\'abonnement:', error);
      throw error;
    }
  },

  async sendInvitationEmail(email: string, role: 'admin' | 'user', language: Language = 'fr') {
    try {
      console.log('📧 Envoi d\'email d\'invitation via Cloud Function:', { email, role, language });
      
      // Préparer le contenu de l'email
      const template = invitationTemplates[language];
      const baseUrl = getBaseUrl();
      const inviteUrl = `${baseUrl}/invitation?email=${encodeURIComponent(email)}&role=${role}`;
      const htmlContent = template.content(inviteUrl);
      
      // Appeler la fonction Cloud Firebase spécifique pour les invitations
      const sendInvitationEmailFn = httpsCallable(functions, 'sendInvitationEmail');
      const result = await sendInvitationEmailFn({ 
        email, 
        role,
        language,
        inviteUrl
      });
      
      console.log('✅ Email d\'invitation envoyé avec succès:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi d\'email d\'invitation:', error);
      
      // Fallback: utiliser la fonction générique en cas d'erreur
      console.log('📧 Utilisation de la fonction générique pour l\'email d\'invitation');
      const template = invitationTemplates[language];
      const baseUrl = getBaseUrl();
      const inviteUrl = `${baseUrl}/invitation?email=${encodeURIComponent(email)}&role=${role}`;
      const htmlContent = template.content(inviteUrl);
      
      const sendEmailFn = httpsCallable(functions, 'sendWelcomeEmail');
      const fallbackResult = await sendEmailFn({ 
        email, 
        name: 'Utilisateur', 
        language,
        customSubject: template.subject,
        customHtml: htmlContent
      });
      
      return fallbackResult.data;
    }
  },

  async sendTrialActivationEmail(email: string, endDate: Date, language: Language = 'fr', durationInDays: number) {
    try {
      console.log('📧 Envoi d\'email d\'activation d\'essai via Cloud Function:', { email, endDate, language, durationInDays });
      
      // Préparer le contenu de l'email
      const template = trialTemplates[language];
      const baseUrl = getBaseUrl();
      const logoUrl = `${baseUrl}/logo.png`;
      
      const formattedDate = new Intl.DateTimeFormat(language === 'fr' ? 'fr-FR' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(endDate);
      
      const htmlContent = template.content(baseUrl, logoUrl, formattedDate, durationInDays);
      
      // Appeler la fonction Cloud Firebase
      const sendEmailFn = httpsCallable(functions, 'sendWelcomeEmail');
      const result = await sendEmailFn({ 
        email, 
        name: 'Utilisateur', 
        language,
        customSubject: template.subject,
        customHtml: htmlContent
      });
      
      console.log('✅ Email d\'activation d\'essai envoyé avec succès:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi d\'email d\'activation d\'essai:', error);
      throw error;
    }
  },
  
  /**
   * Envoie un email simple sans vérification d'invitation ni modification dans la base de données
   * @param email Adresse email du destinataire
   * @param subject Sujet de l'email
   * @param htmlContent Contenu HTML de l'email
   * @param language Langue de l'email (fr par défaut)
   * @returns Résultat de l'envoi
   */
  async sendSimpleEmail(email: string, subject: string, htmlContent: string, language: Language = 'fr') {
    try {
      console.log('📧 Envoi d\'email simple via Cloud Function:', { email, subject, language });
      
      // Appeler la fonction Cloud Firebase
      const sendNotificationEmailFn = httpsCallable(functions, 'sendNotificationEmail');
      const result = await sendNotificationEmailFn({ 
        email, 
        subject,
        htmlContent,
        language
      });
      
      console.log('✅ Email simple envoyé avec succès:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Erreur lors de l\'envoi d\'email simple:', error);
      throw error;
    }
  }
};
